import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from '@/ui/common/Icons';
import styles from './styles.module.scss';

/**
 * Radix UIのセレクトボックス
 *
 * //TODO: リファクタが必ず必要
 *
 * @see https://www.radix-ui.com/primitives/docs/components/select
 */
const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectIcon = SelectPrimitive.Icon;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    /** クリアボタンがクリックされたときに呼び出されるコールバック関数 */
    onClear?: () => void;
    /** 値が選択されているかどうかを示すフラグ（クリアボタンの表示制御に使用） */
    hasValue?: boolean;
    /** クリア機能を有効にするかどうかのフラグ */
    clearable?: boolean;
  }
>(({ className, children, onClear, hasValue, clearable, ...props }, ref) => (
  <SelectPrimitive.Trigger ref={ref} className={`${styles.trigger} ${className || ''}`} {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon size='xs' className={styles.triggerIcon} />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));

type ClearableSelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> & {
  /** 選択をクリアするコールバック */
  onClear: () => void;
  /** 値が選択されているかどうか（クリアボタンの表示制御に使用） */
  hasValue: boolean;
};

/**
 * クリアボタン付きの SelectTrigger。
 * SelectPrimitive.Trigger（内部で button をレンダー）の外側に兄弟要素として
 * クリアボタンを配置することで、button のネストによる invalid HTML を回避する。
 */
const ClearableSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  ClearableSelectTriggerProps
>(({ onClear, hasValue, className, children, ...props }, ref) => (
  <div className={styles.triggerWrapper}>
    <SelectTrigger
      ref={ref}
      className={`${hasValue ? styles.clearableTrigger : ''} ${className ?? ''}`.trim()}
      {...props}
    >
      {children}
    </SelectTrigger>
    {hasValue && (
      <button
        type='button'
        className={styles.clearButton}
        onPointerDown={e => e.stopPropagation()}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClear();
        }}
        aria-label='選択をクリア'
      >
        <CrossIcon size='xs' />
      </button>
    )}
  </div>
));
ClearableSelectTrigger.displayName = 'ClearableSelectTrigger';

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={`${styles.scrollButton} ${className || ''}`}
    {...props}
  >
    <ChevronUpIcon className={styles.scrollIcon} />
  </SelectPrimitive.ScrollUpButton>
));

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={`${styles.scrollButton} ${className || ''}`}
    {...props}
  >
    <ChevronDownIcon size='xs' />
  </SelectPrimitive.ScrollDownButton>
));

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    /**
     * Viewport の DOM 要素を受け取るコールバック ref
     * ページネーション対応で IntersectionObserver の root として使用する
     */
    viewportRef?: React.RefCallback<HTMLDivElement>;
  }
>(
  (
    {
      className,
      children,
      position = 'popper' as 'item-aligned' | 'popper',
      viewportRef,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={`${styles.content} ${position === 'popper' ? styles.contentPopper : ''} ${className || ''}`}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          ref={viewportRef}
          className={`${styles.viewport} ${position === 'popper' ? styles.viewportPopper : ''}`}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
);

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={`${styles.label} ${className || ''}`} {...props} />
));

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { value: string }
>(({ className, children, value, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={`${styles.item} ${className || ''}`}
    value={value}
    {...props}
  >
    <span className={styles.itemIndicator}>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className={styles.checkIcon} />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={`${styles.separator} ${className || ''}`}
    {...props}
  />
));

export {
  ClearableSelectTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectIcon,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
